"use client";

import { useState, useEffect, useRef } from "react";
import { Sidebar } from "./sidebar";
import { InputCanvas } from "@/components/canvas";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, PanelLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "@/components/error-boundary";
import { useLesson } from "@/contexts/lesson-context";
import { useChatMessages } from "@ai-sdk-tools/store";
import type { UIMessage } from "@ai-sdk/react";
import { useChatInterface } from "@/hooks/use-chat-interface";

interface AppShellProps {
  children: React.ReactNode;
}

const MINIMIZED_WIDTH = 60;
const DEFAULT_SIDEBAR_WIDTH = 280;
const DEFAULT_INPUT_WIDTH = 525;
const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 600;
const MIN_INPUT_WIDTH = 250;
const MAX_INPUT_WIDTH = 1200;

export function AppShell({ children }: AppShellProps) {
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [inputWidth, setInputWidth] = useState(DEFAULT_INPUT_WIDTH);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [inputCollapsed, setInputCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [fetchedMessages, setFetchedMessages] = useState<UIMessage[] | null>(null);
  const { currentLesson, currentMiniLesson } = useLesson();
  const { chatId } = useChatInterface();
  const messages = useChatMessages() as unknown as UIMessage[];
  const sidebarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const sidebarWidthRef = useRef(DEFAULT_SIDEBAR_WIDTH);
  const inputWidthRef = useRef(DEFAULT_INPUT_WIDTH);
  const sidebarResizeRef = useRef<HTMLDivElement>(null);
  const inputResizeRef = useRef<HTMLDivElement>(null);
  const allMessages =
    Array.isArray(messages) && messages.length > 0
      ? messages
      : (fetchedMessages ?? []);
  const hasArtifacts = false; // Artifact panel removed; kept for compatibility

  // Track mobile layout (matches lg breakpoint)
  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  // Default to collapsed panels on mobile for space
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
      setInputCollapsed(true);
    }
  }, [isMobile]);

  // Open middle panel when lesson or mini lesson content is selected.
  useEffect(() => {
    if (currentLesson || currentMiniLesson) {
      setInputCollapsed(false);
    }
  }, [currentLesson, currentMiniLesson]);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!chatId) {
        setFetchedMessages(null);
        return;
      }
      if (Array.isArray(messages) && messages.length > 0) return;
      try {
        const res = await fetch(`/api/chats/${chatId}/messages`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) {
          setFetchedMessages(data as UIMessage[]);
        }
      } catch {
        // ignore
      }
    };
    fetchMessages();
  }, [chatId, messages]);

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  useEffect(() => {
    inputWidthRef.current = inputWidth;
  }, [inputWidth]);

  // Sidebar resize handler
  useEffect(() => {
    const sidebarResizer = sidebarResizeRef.current;
    if (!sidebarResizer || sidebarCollapsed) return;

    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isResizing = true;
      startX = e.clientX;
      startWidth = sidebarWidthRef.current;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const diff = e.clientX - startX;
      const newWidth = startWidth + diff;
      if (newWidth >= MIN_SIDEBAR_WIDTH && newWidth <= MAX_SIDEBAR_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      isResizing = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    sidebarResizer.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      sidebarResizer.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [sidebarCollapsed]);

  // Input column resize handler
  useEffect(() => {
    const inputResizer = inputResizeRef.current;
    if (!inputResizer || inputCollapsed) return;

    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isResizing = true;
      startX = e.clientX;
      startWidth = inputWidthRef.current;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const diff = e.clientX - startX;
      const newWidth = startWidth + diff;
      if (newWidth >= MIN_INPUT_WIDTH && newWidth <= MAX_INPUT_WIDTH) {
        setInputWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      isResizing = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    inputResizer.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      inputResizer.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [inputCollapsed]);

  const effectiveSidebarWidth = sidebarCollapsed ? MINIMIZED_WIDTH : sidebarWidth;
  const effectiveInputWidth = inputCollapsed ? MINIMIZED_WIDTH : inputWidth;

  return (
    <div className="h-screen bg-[var(--mindup-off-white)] dark:bg-[var(--mindup-soft-black)] flex flex-col lg:flex-row gap-2 sm:gap-3 p-2 sm:p-3 relative overflow-hidden">
      {/* Large decorative blob shapes — brand color families */}
      <div className="fixed top-[10%] left-[60%] w-[600px] h-[600px] bg-gradient-to-br from-[var(--mindup-pink-light)]/30 to-[var(--mindup-mint)]/20 blob-shape pointer-events-none blur-3xl" />
      <div className="fixed bottom-[10%] left-[20%] w-[500px] h-[500px] bg-gradient-to-tr from-[var(--mindup-mint)]/25 to-[var(--mindup-blue-light)]/15 blob-shape-2 pointer-events-none blur-3xl" />
      <div className="fixed top-[60%] right-[10%] w-[400px] h-[400px] bg-gradient-to-bl from-[var(--mindup-pink-light)]/20 to-[var(--mindup-blue)]/10 blob-shape-3 pointer-events-none blur-3xl" />

      {/* Left Sidebar Panel */}
      <aside
        ref={sidebarRef}
        style={{
          width: isMobile ? "100%" : `${effectiveSidebarWidth}px`,
          transition: "width 0.2s ease",
        }}
        className={cn(
          "order-0 relative bg-[#7DDBBF] backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)] border border-gray-200/50 dark:border-white/5 z-20 flex-shrink-0 overflow-hidden hidden lg:flex flex-col",
          isMobile ? "min-h-[56px]" : "",
        )}
      >
        {sidebarCollapsed ? (
          <div className={cn("h-full flex flex-col items-center gap-2", isMobile ? "py-2" : "py-4")}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(false)}
              className="text-[var(--mindup-soft-black)] dark:text-white hover:bg-[var(--mindup-pink)]/10 h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="flex-1 flex flex-col items-center gap-2">
              <PanelLeft className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        ) : (
          <>
            <div className="absolute top-3 right-3 z-50">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(true)}
                className="h-7 w-7 opacity-60 hover:opacity-100 text-[var(--mindup-soft-black)] dark:text-white hover:bg-[var(--mindup-pink)]/10"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className={cn("h-full overflow-auto pt-10", isMobile ? "max-h-[45vh]" : "")}>
              <ErrorBoundary fallbackTitle="Sidebar error" fallbackClassName="h-full flex items-center justify-center p-4">
                <Sidebar open={true} onToggle={() => setSidebarCollapsed(true)} />
              </ErrorBoundary>
            </div>
            <div
              ref={sidebarResizeRef}
              className="absolute -right-2 top-0 bottom-0 w-4 cursor-col-resize hover:bg-[var(--mindup-pink)]/30 transition-colors z-40 rounded-full"
              style={{ touchAction: "none" }}
            />
          </>
        )}
      </aside>

      {/* Left Input/Curriculum Panel */}
      <aside
        ref={inputRef}
        style={{
          width: isMobile ? "100%" : `${effectiveInputWidth}px`,
          transition: "width 0.2s ease",
        }}
        className={cn(
          "order-0 relative bg-[#6495FF] dark:bg-[#6495FF] backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)] border border-gray-200/50 dark:border-white/5 z-20 flex-shrink-0 overflow-hidden hidden lg:flex flex-col",
          isMobile ? "min-h-[56px]" : "",
        )}
      >
        {inputCollapsed ? (
          <div className={cn("h-full flex flex-col items-center gap-2", isMobile ? "py-2" : "py-4")}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setInputCollapsed(false)}
              className="text-white hover:bg-white/20 h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="flex-1 flex flex-col items-center gap-2">
              <BookOpen className="h-5 w-5 text-white/80" />
            </div>
          </div>
        ) : (
          <>
            <div className="absolute top-3 right-3 z-50">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setInputCollapsed(true)}
                className="h-7 w-7 opacity-80 hover:opacity-100 text-white hover:bg-white/20"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className={cn("h-full overflow-auto pt-10 px-3 pb-3", isMobile ? "max-h-[45vh]" : "")}>
              <ErrorBoundary fallbackTitle="Curriculum panel error" fallbackClassName="h-full flex items-center justify-center p-4">
                <InputCanvas />
              </ErrorBoundary>
            </div>
            <div
              ref={inputResizeRef}
              className="absolute -right-2 top-0 bottom-0 w-4 cursor-col-resize hover:bg-white/30 transition-colors z-40 rounded-full"
              style={{ touchAction: "none" }}
            />
          </>
        )}
      </aside>

      {/* Center Chat Area */}
      <main className="order-1 flex-1 min-w-0 overflow-hidden flex flex-col bg-white/98 dark:bg-[#363636]/98 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)] border border-gray-200/50 dark:border-white/5">
        {children}
      </main>

    </div>
  );
}

